// AdminLogin — Admin portal login (dark theme, separate from customer)
// Layout: centered card, admin shield, demo credentials hint

import { useState } from 'react';

export function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('admin@funderpro.live');
  const [password, setPassword] = useState('admin1234');
  const [code, setCode] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function submit() {
    setError('');
    if (show2FA) {
      if (code !== '000000') { setError('Invalid 2FA code (use 000000)'); return; }
      localStorage.setItem('admin_token', 'demo_admin_' + Date.now());
      onLogin();
      return;
    }
    if (email !== 'admin@funderpro.live' || password !== 'admin1234') {
      setError('Invalid credentials (use admin@funderpro.live / admin1234)');
      return;
    }
    setLoading(true);
    setTimeout(() => { setLoading(false); setShow2FA(true); }, 600);
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'radial-gradient(ellipse at top, #1C1C1E 0%, #000 50%)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-[16px] bg-gradient-to-br from-[#FF3B30] to-[#FF9500] text-white text-[28px] mb-3">🛡️</div>
          <h1 className="text-[24px] font-bold text-white tracking-tight">Funder Pro Admin</h1>
          <p className="text-[12px] text-white/50 mt-1">Restricted access · Authorized personnel only</p>
        </div>

        {/* Card */}
        <div className="rounded-[20px] bg-[#1C1C1E] border border-white/10 p-5">
          {!show2FA ? (
            <>
              <h2 className="text-[18px] font-bold text-white mb-4">Sign in</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Email</label>
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="admin-email"
                    className="w-full h-11 rounded-[10px] bg-black/40 border border-white/10 px-3 text-[14px] text-white focus:outline-none focus:border-[#FF3B30] placeholder:text-white/30"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Password</label>
                  <input
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-password"
                    className="w-full h-11 rounded-[10px] bg-black/40 border border-white/10 px-3 text-[14px] text-white focus:outline-none focus:border-[#FF3B30] placeholder:text-white/30"
                  />
                </div>
              </div>
              {error && <div className="mt-3 rounded-[8px] bg-[#FF3B30]/10 border border-[#FF3B30]/20 px-3 py-2 text-[12px] text-[#FF3B30]">{error}</div>}
              <button
                onClick={submit} disabled={loading}
                data-testid="admin-login"
                className="mt-4 flex h-11 w-full items-center justify-center rounded-[10px] bg-gradient-to-r from-[#FF3B30] to-[#FF9500] text-[14px] font-bold text-white disabled:opacity-40 active:opacity-60"
              >
                {loading ? 'Verifying...' : 'Continue'}
              </button>
            </>
          ) : (
            <>
              <h2 className="text-[18px] font-bold text-white mb-1">Two-factor authentication</h2>
              <p className="text-[12px] text-white/50 mb-4">Enter the 6-digit code from your authenticator app</p>
              <div className="flex items-center justify-center gap-1.5 mb-3" data-testid="admin-2fa">
                {[0,1,2,3,4,5].map((i) => (
                  <input
                    key={i} value={code[i] || ''} maxLength={1}
                    onChange={(e) => { const v = e.target.value; setCode((c) => { const n = c.split(''); n[i] = v; return n.join(''); }); }}
                    data-testid={`admin-2fa-${i}`}
                    className="h-12 w-10 rounded-[8px] bg-black/40 border border-white/10 text-center text-[18px] font-bold text-white focus:outline-none focus:border-[#FF3B30]"
                  />
                ))}
              </div>
              <div className="rounded-[8px] bg-[#FF9500]/10 border border-[#FF9500]/20 px-3 py-2 text-[11px] text-[#FF9500] mb-3">
                💡 Demo: enter <code className="font-mono font-bold">000000</code>
              </div>
              {error && <div className="mb-3 rounded-[8px] bg-[#FF3B30]/10 border border-[#FF3B30]/20 px-3 py-2 text-[12px] text-[#FF3B30]">{error}</div>}
              <button onClick={submit} data-testid="admin-verify" className="flex h-11 w-full items-center justify-center rounded-[10px] bg-gradient-to-r from-[#FF3B30] to-[#FF9500] text-[14px] font-bold text-white">
                Verify & sign in
              </button>
            </>
          )}

          <div className="mt-4 rounded-[8px] bg-white/5 border border-white/10 px-3 py-2 text-[11px] text-white/60">
            <div className="font-semibold text-white/80 mb-0.5">Demo credentials</div>
            <div>📧 admin@funderpro.live</div>
            <div>🔑 admin1234</div>
            <div>🛡️ 2FA code: 000000</div>
          </div>
        </div>

        <div className="text-center mt-4 text-[10px] text-white/30">
          By signing in you agree to our audit log. All actions are recorded.
        </div>
      </div>
    </div>
  );
}
